'use client';

import { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useRouter, useSearchParams } from 'next/navigation';
import Link from 'next/link';
import { toast } from 'sonner';

const brandFeatures = [
  { icon: '✦', label: 'AI-Powered Concierge', desc: 'Personalised travel intelligence at your fingertips' },
  { icon: '◈', label: 'Private Rate Access', desc: 'Exclusive pricing across 180+ luxury destinations' },
  { icon: '⬡', label: 'Multi-Portal Platform', desc: 'Customer, Merchant, and Enterprise dashboards' },
];

export default function LoginContent() {
  const { signIn, signInWithGoogle } = useAuth();
  const router = useRouter();
  const searchParams = useSearchParams();
  const next = searchParams.get('next') || '/customer-portal';

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      const result = await signIn(email, password);
      toast.success('Welcome back to WorldwayLuxe');
      // Determine redirect based on role from session
      const user = result?.user;
      const role = user?.user_metadata?.role || user?.app_metadata?.role || 'customer';
      let redirectPath = next;
      if (next === '/customer-portal') {
        if (role === 'super_admin') redirectPath = '/super-admin';
        else if (role === 'admin') redirectPath = '/enterprise-admin-platform';
        else if (role === 'merchant') redirectPath = '/merchant-portal';
        else if (role === 'supplier') redirectPath = '/supplier-portal';
      }
      router.push(redirectPath);
    } catch (err: unknown) {
      const error = err as { message?: string };
      toast.error(error?.message || 'Sign in failed. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setGoogleLoading(true);
    try {
      await signInWithGoogle();
    } catch (err: unknown) {
      const error = err as { message?: string };
      toast.error(error?.message || 'Google sign in failed.');
      setGoogleLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-[var(--background)]">

      {/* Left Brand Panel */}
      <div className="hidden lg:flex lg:w-[52%] relative flex-col justify-between p-12 overflow-hidden">
        {/* Background layers */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#050810] via-[#0A0F1E] to-[#0D1525]" />
        <div className="absolute inset-0 opacity-20"
          style={{
            backgroundImage: 'radial-gradient(ellipse 70% 50% at 30% 30%, rgba(201,168,76,0.18) 0%, transparent 70%)',
          }}
        />
        {/* Grid texture */}
        <div className="absolute inset-0 opacity-[0.035]"
          style={{
            backgroundImage: 'linear-gradient(var(--border) 1px, transparent 1px), linear-gradient(90deg, var(--border) 1px, transparent 1px)',
            backgroundSize: '48px 48px',
          }}
        />
        {/* Gold orb */}
        <div className="absolute top-1/3 left-1/3 w-80 h-80 rounded-full opacity-[0.07]"
          style={{ background: 'radial-gradient(circle, var(--primary) 0%, transparent 70%)', filter: 'blur(50px)' }}
        />
        {/* Bottom orb */}
        <div className="absolute bottom-1/4 right-1/4 w-48 h-48 rounded-full opacity-[0.05]"
          style={{ background: 'radial-gradient(circle, var(--accent) 0%, transparent 70%)', filter: 'blur(40px)' }}
        />

        {/* Content */}
        <div className="relative z-10">
          {/* Logo */}
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg gold-gradient flex items-center justify-center">
              <span className="text-[var(--background)] text-xs font-bold">W</span>
            </div>
            <span className="text-[var(--primary)] font-bold tracking-widest text-sm uppercase" style={{ fontFamily: 'var(--font-serif)' }}>
              WorldwayLuxe
            </span>
          </div>
        </div>

        <div className="relative z-10 flex-1 flex flex-col justify-center py-12">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 border border-[rgba(201,168,76,0.25)] rounded-full px-3 py-1 mb-8 text-xs tracking-widest uppercase text-[var(--primary)] w-fit"
            style={{ background: 'rgba(201,168,76,0.05)' }}>
            <span className="w-1.5 h-1.5 rounded-full bg-[var(--primary)] animate-pulse" />
            Enterprise Platform
          </div>

          <h1 className="text-4xl xl:text-5xl font-bold leading-tight mb-4" style={{ fontFamily: 'var(--font-serif)' }}>
            <span className="text-[var(--foreground)]">The World,</span>
            <br />
            <span className="gold-text-gradient">Curated for You</span>
          </h1>

          <p className="text-[var(--muted-foreground)] text-base leading-relaxed mb-12 max-w-sm">
            Access the enterprise AI travel commerce platform trusted by the world's most discerning travellers and luxury operators.
          </p>

          {/* Features */}
          <div className="space-y-5">
            {brandFeatures.map((f) => (
              <div key={f.label} className="flex items-start gap-4">
                <div className="w-8 h-8 rounded-lg border border-[rgba(201,168,76,0.2)] flex items-center justify-center flex-shrink-0 text-[var(--primary)] text-sm"
                  style={{ background: 'rgba(201,168,76,0.06)' }}>
                  {f.icon}
                </div>
                <div>
                  <div className="text-sm font-semibold text-[var(--foreground)] mb-0.5">{f.label}</div>
                  <div className="text-xs text-[var(--muted-foreground)]">{f.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Bottom stats */}
        <div className="relative z-10 grid grid-cols-3 gap-4 pt-8 border-t border-[rgba(201,168,76,0.1)]">
          {[['180+', 'Destinations'], ['12K+', 'Journeys'], ['98%', 'Satisfaction']].map(([val, lbl]) => (
            <div key={lbl} className="text-center">
              <div className="text-lg font-bold text-[var(--primary)]" style={{ fontFamily: 'var(--font-serif)' }}>{val}</div>
              <div className="text-2xs text-[var(--muted-foreground)] tracking-wide uppercase mt-0.5">{lbl}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Right Form Panel */}
      <div className="flex-1 flex flex-col items-center justify-center px-6 py-12 relative">
        {/* Subtle background */}
        <div className="absolute inset-0 bg-[var(--surface-2)]" />
        <div className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage: 'radial-gradient(ellipse 60% 40% at 80% 20%, rgba(201,168,76,0.3) 0%, transparent 70%)',
          }}
        />

        <div className="relative z-10 w-full max-w-md">
          {/* Mobile logo */}
          <div className="lg:hidden text-center mb-8">
            <div className="inline-flex items-center gap-2 mb-2">
              <div className="w-7 h-7 rounded-lg gold-gradient flex items-center justify-center">
                <span className="text-[var(--background)] text-xs font-bold">W</span>
              </div>
              <span className="text-[var(--primary)] font-bold tracking-widest text-sm uppercase" style={{ fontFamily: 'var(--font-serif)' }}>
                WorldwayLuxe
              </span>
            </div>
            <p className="text-xs text-[var(--muted-foreground)] tracking-widest uppercase">Enterprise AI Travel Commerce</p>
          </div>

          {/* Header */}
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-[var(--foreground)] mb-1" style={{ fontFamily: 'var(--font-serif)' }}>
              Welcome back
            </h2>
            <p className="text-sm text-[var(--muted-foreground)]">Sign in to your WorldwayLuxe account</p>
          </div>

          {/* Demo Credentials */}
          <div className="rounded-xl p-4 mb-6 text-xs space-y-1.5 border border-[rgba(201,168,76,0.15)]"
            style={{ background: 'rgba(201,168,76,0.04)' }}>
            <p className="font-semibold text-[var(--primary)] mb-2 tracking-wide uppercase text-2xs">Demo Access</p>
            <div className="space-y-1 text-[var(--muted-foreground)]">
              <p><span className="text-[var(--foreground)]">Super Admin:</span> worldwayluxe@gmail.com / WorldwayLuxe@2026!</p>
              <p><span className="text-[var(--foreground)]">Admin:</span> admin@worldwayluxe.com / Admin@2026!</p>
              <p><span className="text-[var(--foreground)]">Customer:</span> customer@worldwayluxe.com / Customer@2026!</p>
              <p><span className="text-[var(--foreground)]">Merchant:</span> merchant@worldwayluxe.com / Merchant@2026!</p>
            </div>
          </div>

          {/* Google Sign In */}
          <button
            onClick={handleGoogleSignIn}
            disabled={googleLoading}
            className="w-full flex items-center justify-center gap-3 rounded-xl py-3 text-sm font-medium text-[var(--foreground)] border border-[var(--border)] hover:border-[rgba(201,168,76,0.3)] hover:bg-[var(--surface-3)] transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed mb-5"
            style={{ background: 'var(--surface-3)' }}
          >
            <svg className="w-4 h-4 flex-shrink-0" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
            </svg>
            {googleLoading ? 'Redirecting...' : 'Continue with Google'}
          </button>

          {/* Divider */}
          <div className="relative mb-5">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-[var(--border)]" />
            </div>
            <div className="relative flex justify-center text-xs">
              <span className="px-3 text-[var(--muted-foreground)]" style={{ background: 'var(--surface-2)' }}>or sign in with email</span>
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleSignIn} className="space-y-4">
            <div>
              <label className="block text-xs font-medium text-[var(--muted-foreground)] mb-1.5 tracking-wide uppercase">Email Address</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="you@example.com"
                className="w-full bg-[var(--surface-3)] border border-[var(--border)] rounded-xl px-4 py-3 text-[var(--foreground)] text-sm placeholder-[var(--muted-foreground)] focus:outline-none focus:border-[var(--primary)] focus:ring-1 focus:ring-[rgba(201,168,76,0.2)] transition-all"
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="block text-xs font-medium text-[var(--muted-foreground)] tracking-wide uppercase">Password</label>
                <Link href="/auth/reset-password" className="text-xs text-[var(--primary)] hover:underline">
                  Forgot password?
                </Link>
              </div>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  placeholder="••••••••"
                  className="w-full bg-[var(--surface-3)] border border-[var(--border)] rounded-xl px-4 py-3 pr-11 text-[var(--foreground)] text-sm placeholder-[var(--muted-foreground)] focus:outline-none focus:border-[var(--primary)] focus:ring-1 focus:ring-[rgba(201,168,76,0.2)] transition-all"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--muted-foreground)] hover:text-[var(--foreground)] transition-colors p-1"
                >
                  {showPassword ? (
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88" />
                    </svg>
                  ) : (
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  )}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full btn-primary py-3.5 rounded-xl text-sm font-semibold shadow-[0_4px_20px_rgba(201,168,76,0.2)] disabled:opacity-50 disabled:cursor-not-allowed mt-2"
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  Signing in...
                </span>
              ) : 'Sign In to Platform'}
            </button>
          </form>

          <p className="text-center text-sm text-[var(--muted-foreground)] mt-6">
            New to WorldwayLuxe?{' '}
            <Link href="/auth/register" className="text-[var(--primary)] hover:underline font-medium">
              Create account
            </Link>
          </p>

          {/* Back to home */}
          <div className="text-center mt-4">
            <Link href="/" className="text-xs text-[var(--muted-foreground)] hover:text-[var(--primary)] transition-colors">
              ← Back to homepage
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
